/**
 * QR Audit Page — surfaces asset QR label audit events.
 *
 * Data source: existing /api/audit endpoint, no new backend. Filters
 * client-side for actions whose name starts with `asset_qr_`
 * (asset_qr_issued, asset_qr_revoked, asset_qr_label_printed,
 * asset_qr_label_printed_bulk, asset_qr_resolved).
 *
 * Reuses polish-pass primitives: EmptyState, TableSkeleton, IconButton.
 */
import React, { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { PaginationFooter } from '@/components/shared/PaginationFooter';
import { EmptyState } from '@/components/common/EmptyState';
import { TableSkeleton } from '@/components/common/TableSkeleton';
import { IconButton } from '@/components/common/IconButton';
import { QrCode, FileText, Search, Printer, Tag, ShieldCheck, ShieldX, ScanLine } from 'lucide-react';
import { toast } from 'sonner';

// Known QR-related audit action prefixes (kept in sync with
// /app/backend/routes/asset_qr_labels.py's audit_write calls).
const QR_ACTIONS = [
  { code: 'asset_qr_issued', label: 'QR Issued', icon: Tag, tone: 'positive' },
  { code: 'asset_qr_revoked', label: 'QR Revoked', icon: ShieldX, tone: 'critical' },
  { code: 'asset_qr_label_printed', label: 'Label Printed', icon: Printer, tone: 'info' },
  { code: 'asset_qr_label_printed_bulk', label: 'Bulk Print', icon: Printer, tone: 'info' },
  { code: 'asset_qr_resolved', label: 'QR Scanned', icon: ScanLine, tone: 'neutral' },
];
const ACTION_MAP = Object.fromEntries(QR_ACTIONS.map(a => [a.code, a]));

function SummaryTile({ label, value, icon: Icon, tone = 'neutral', testid }) {
  const tones = {
    neutral: 'border-slate-200 bg-white',
    positive: 'border-emerald-200 bg-emerald-50/40 text-emerald-700',
    info: 'border-sky-200 bg-sky-50/40 text-sky-700',
    critical: 'border-red-200 bg-red-50/40 text-red-700',
    warn: 'border-amber-200 bg-amber-50/40 text-amber-700',
  };
  return (
    <div data-testid={testid} className={`rounded-lg border p-3 ${tones[tone].split(' ').slice(0,2).join(' ')}`}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-medium uppercase tracking-wider text-slate-500">{label}</span>
        {Icon && <Icon className="h-3.5 w-3.5 text-slate-400" aria-hidden />}
      </div>
      <div className={`mt-1 text-xl font-semibold tabular-nums ${tones[tone].split(' ').slice(2).join(' ') || 'text-slate-900'}`}>{value}</div>
    </div>
  );
}

export default function QrAuditPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      // The /audit endpoint accepts `entity` (singular). QR audits are
      // written with entity='asset'. We fetch a larger window and filter
      // client-side for the qr_* actions because the endpoint doesn't
      // support multi-action filtering.
      const params = new URLSearchParams({
        page: String(page),
        pageSize: '100',
        entity: 'asset',
        sortBy: 'timestamp',
        sortDir: 'desc',
      });
      if (actionFilter !== 'all') params.set('action', actionFilter);
      const res = await api.get(`/audit?${params.toString()}`);
      const items = (res.data.items || res.data.data || []).filter(l =>
        (l.action || '').toString().toLowerCase().startsWith('asset_qr_')
      );
      setLogs(items);
      setTotal(res.data.total || items.length);
    } catch (e) {
      toast.error('Failed to load QR audit events');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [page, actionFilter]); // eslint-disable-line

  const filtered = useMemo(() => logs.filter(l => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (l.user_name || '').toLowerCase().includes(s)
      || (l.entity_id || '').toLowerCase().includes(s)
      || (l.action || '').toLowerCase().includes(s)
      || JSON.stringify(l.meta || {}).toLowerCase().includes(s);
  }), [logs, search]);

  const kpis = useMemo(() => {
    const by = { issued: 0, revoked: 0, printed: 0, scanned: 0 };
    for (const l of filtered) {
      const a = (l.action || '').toLowerCase();
      if (a === 'asset_qr_issued') by.issued += 1;
      else if (a === 'asset_qr_revoked') by.revoked += 1;
      else if (a === 'asset_qr_label_printed' || a === 'asset_qr_label_printed_bulk') by.printed += 1;
      else if (a === 'asset_qr_resolved') by.scanned += 1;
    }
    return by;
  }, [filtered]);

  const hasFilter = actionFilter !== 'all' || !!search;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="qr-audit-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <QrCode className="h-5 w-5 text-primary" /> QR Audit
        </h1>
        <p className="text-muted-foreground mt-1">Asset QR label issuance, revocation, printing, and scan history.</p>
      </div>

      {/* KPI band */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="qr-audit-summary">
        <SummaryTile testid="qr-audit-summary-issued" label="Issued" value={kpis.issued} icon={ShieldCheck} tone="positive" />
        <SummaryTile testid="qr-audit-summary-revoked" label="Revoked" value={kpis.revoked} icon={ShieldX} tone={kpis.revoked > 0 ? 'critical' : 'neutral'} />
        <SummaryTile testid="qr-audit-summary-printed" label="Labels printed" value={kpis.printed} icon={Printer} tone="info" />
        <SummaryTile testid="qr-audit-summary-scanned" label="Scans resolved" value={kpis.scanned} icon={ScanLine} />
      </div>

      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 md:w-[260px] md:flex-none">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search user, asset, action..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9"
              data-testid="qr-audit-search"
            />
          </div>
          <Select value={actionFilter} onValueChange={v => { setActionFilter(v); setPage(1); }}>
            <SelectTrigger className="w-[200px]" data-testid="qr-audit-action-filter">
              <SelectValue placeholder="Action" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All QR actions</SelectItem>
              {QR_ACTIONS.map(a => (
                <SelectItem key={a.code} value={a.code}>{a.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <p className="text-sm text-muted-foreground">{filtered.length} event(s)</p>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <TableSkeleton rows={6} columns={5} testid="qr-audit-loading" />
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={QrCode}
              title={hasFilter ? 'No QR events match these filters' : 'No QR audit events yet'}
              description={hasFilter ? 'Try clearing the search or action filter.' : 'Asset QR label events will appear here as they are issued, revoked, printed, or scanned.'}
              testid="qr-audit-empty"
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Asset</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(l => {
                  const def = ACTION_MAP[l.action] || { label: l.action, icon: QrCode, tone: 'neutral' };
                  const Icon = def.icon;
                  const toneClass = {
                    positive: 'bg-emerald-50 text-emerald-700 border-emerald-200',
                    critical: 'bg-red-50 text-red-700 border-red-200',
                    info: 'bg-sky-50 text-sky-700 border-sky-200',
                    warn: 'bg-amber-50 text-amber-700 border-amber-200',
                    neutral: 'bg-slate-50 text-slate-700 border-slate-200',
                  }[def.tone || 'neutral'];
                  return (
                    <TableRow
                      key={l.id}
                      className="hover:bg-muted/60 cursor-pointer"
                      onClick={() => setDetail(l)}
                      data-testid={`qr-audit-row-${l.id}`}
                    >
                      <TableCell className="font-mono text-xs tabular-nums">{l.timestamp?.replace('T', ' ').slice(0, 19)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-[10px] gap-1 ${toneClass}`}>
                          <Icon className="h-3 w-3" />
                          {def.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {l.entity_id?.slice(0, 14)}{l.entity_id?.length > 14 ? '…' : ''}
                      </TableCell>
                      <TableCell className="text-sm">{l.user_name || l.user_id?.slice(0, 8) || '—'}</TableCell>
                      <TableCell>
                        <IconButton
                          label="View detail"
                          onClick={(e) => { e.stopPropagation(); setDetail(l); }}
                          testid={`qr-audit-detail-${l.id}`}
                        >
                          <FileText className="h-3 w-3" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
          <PaginationFooter page={page} pageSize={100} total={total} onPageChange={setPage} />
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto" data-testid="qr-audit-detail-dialog">
          <DialogHeader>
            <DialogTitle>{ACTION_MAP[detail?.action]?.label || detail?.action}</DialogTitle>
            <DialogDescription>
              {detail?.timestamp} · asset {detail?.entity_id}
            </DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-muted-foreground">User</Label><p className="font-medium">{detail.user_name || detail.user_id}</p></div>
                <div><Label className="text-xs text-muted-foreground">Action</Label><p className="font-mono text-xs">{detail.action}</p></div>
              </div>
              {detail.meta && Object.keys(detail.meta).length > 0 && (
                <div>
                  <Label className="text-xs text-muted-foreground">Metadata</Label>
                  <pre className="mt-1 rounded bg-slate-50 p-3 text-xs overflow-x-auto max-h-60">{JSON.stringify(detail.meta, null, 2)}</pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
